<template>
  <div class="intercom-input-wrapper">
    <label v-if="label" :for="inputId" class="intercom-input-label">
      {{ label }}
      <span v-if="required" class="intercom-input-required">*</span>
    </label>

    <div :class="inputContainerClasses">
      <div v-if="$slots.prefix || prefixIcon" class="intercom-input-prefix">
        <slot name="prefix">
          <svg
            v-if="prefixIcon"
            class="intercom-input-icon"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <component :is="prefixIcon" />
          </svg>
        </slot>
      </div>

      <input
        v-if="type !== 'textarea'"
        ref="inputRef"
        :id="inputId"
        :value="inputValue"
        v-bind="attrs"
        :class="inputClasses"
        :type="computedType"
        :placeholder="placeholder"
        :disabled="disabled"
        :required="required"
        :readonly="readonly"
        :autocomplete="autocomplete"
        @blur="handleBlur"
        @focus="handleFocus"
        @input="handleInput"
      />

      <textarea
        v-else
        ref="inputRef"
        :id="inputId"
        :value="inputValue"
        v-bind="attrs"
        :class="textareaClasses"
        :placeholder="placeholder"
        :disabled="disabled"
        :required="required"
        :readonly="readonly"
        :rows="rows"
        @blur="handleBlur"
        @focus="handleFocus"
        @input="handleInput"
      />

      <!-- Password visibility toggle -->
      <button
        v-if="type === 'password'"
        type="button"
        class="intercom-input-password-toggle"
        @click="togglePasswordVisibility"
        tabindex="-1"
        :aria-label="showPassword ? 'Hide password' : 'Show password'"
      >
        <!-- Eye icon (show password) -->
        <svg
          v-if="!showPassword"
          class="intercom-input-icon"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
          <circle cx="12" cy="12" r="3"></circle>
        </svg>
        <!-- Eye-off icon (hide password) -->
        <svg
          v-else
          class="intercom-input-icon"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
          <line x1="1" y1="1" x2="23" y2="23"></line>
        </svg>
      </button>
      <div v-else-if="$slots.suffix || suffixIcon" class="intercom-input-suffix">
        <slot name="suffix">
          <svg
            v-if="suffixIcon"
            class="intercom-input-icon"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <component :is="suffixIcon" />
          </svg>
        </slot>
      </div>
    </div>

    <p v-if="error" class="intercom-input-error">
      {{ error }}
    </p>
    <p v-else-if="hint" class="intercom-input-hint">
      {{ hint }}
    </p>
  </div>
</template>

<script setup>
import { ref, computed, watch, useSlots, useAttrs } from 'vue'

const slots = useSlots()
const attrs = useAttrs()

defineOptions({
  inheritAttrs: false,
})

const props = defineProps({
  modelValue: {
    type: [String, Number],
    default: '',
  },
  type: {
    type: String,
    default: 'text',
    validator: (value) =>
      ['text', 'email', 'password', 'number', 'tel', 'url', 'search', 'textarea', 'date', 'time'].includes(value),
  },
  label: {
    type: String,
    default: '',
  },
  placeholder: {
    type: String,
    default: '',
  },
  error: {
    type: String,
    default: '',
  },
  valid: {
    type: Boolean,
    default: false,
  },
  hint: {
    type: String,
    default: '',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  required: {
    type: Boolean,
    default: false,
  },
  readonly: {
    type: Boolean,
    default: false,
  },
  autocomplete: {
    type: String,
    default: undefined,
  },
  prefixIcon: {
    type: String,
    default: '',
  },
  suffixIcon: {
    type: String,
    default: '',
  },
  rows: {
    type: Number,
    default: 4,
  },
})

const emit = defineEmits(['update:modelValue', 'blur', 'focus', 'input'])

const inputRef = ref(null)
const inputValue = ref(props.modelValue)
const inputId = ref(`intercom-input-${Math.random().toString(36).substr(2, 9)}`)
const showPassword = ref(false)

defineExpose({
  /** Focus the underlying input/textarea element. */
  focus() {
    inputRef.value?.focus()
  },
  /** Select all text in the underlying input/textarea element. */
  select() {
    inputRef.value?.select()
  },
  /** Get the underlying native input/textarea element. */
  get el() {
    return inputRef.value
  },
})

// Computed type for password visibility toggle
const computedType = computed(() => {
  if (props.type === 'password') {
    return showPassword.value ? 'text' : 'password'
  }
  return props.type
})

const togglePasswordVisibility = () => {
  showPassword.value = !showPassword.value
}

watch(
  () => props.modelValue,
  (newValue) => {
    // Only sync if values are different to prevent cursor jumping during typing
    // Compare as strings to handle "10." vs 10 cases in number inputs
    if (String(newValue) !== String(inputValue.value)) {
      inputValue.value = newValue
    }
  }
)

const inputContainerClasses = computed(() => [
  'intercom-input-container',
  {
    'intercom-input-container--error': props.error,
    'intercom-input-container--valid': props.valid && !props.error,
    'intercom-input-container--disabled': props.disabled,
  },
])

const inputClasses = computed(() => [
  'intercom-input',
  {
    'intercom-input--prefix': props.prefixIcon || slots.prefix,
    'intercom-input--suffix': props.suffixIcon || slots.suffix,
  },
])

const textareaClasses = computed(() => ['intercom-textarea'])

const handleInput = (event) => {
  inputValue.value = event.target.value
  emit('update:modelValue', event.target.value)
  emit('input', event)
}

const handleBlur = (event) => {
  emit('blur', event)
}

const handleFocus = (event) => {
  emit('focus', event)
}
</script>

<style scoped>
/* ========================================
   INTERCOM INPUT BASE
   ======================================== */
.intercom-input-wrapper {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.intercom-input-label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-gray-700);
}

.intercom-input-required {
  color: var(--color-error);
  margin-left: 2px;
}

.intercom-input-container {
  position: relative;
  display: flex;
  align-items: center;
  background: var(--color-white);
  border: 1px solid var(--color-gray-200);
  border-radius: var(--radius-md);
  transition: all var(--transition-base);
}

.intercom-input-container:focus-within {
  background: var(--color-white);
  border-color: var(--color-blue);
  box-shadow: var(--shadow-focus);
}

.intercom-input-container--error {
  background: var(--color-error-bg);
  border-color: var(--color-error);
}

.intercom-input-container--error:focus-within {
  background: var(--color-white);
  border-color: var(--color-error);
  box-shadow: 0 0 0 3px rgba(229, 92, 92, 0.1);
}

.intercom-input-container--valid {
  border-color: var(--color-success);
}

.intercom-input-container--valid:focus-within {
  border-color: var(--color-success);
  box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.1);
}

.intercom-input-container--disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.intercom-input,
.intercom-textarea {
  flex: 1;
  padding: var(--space-2) var(--space-4);
  background: transparent;
  border: none;
  font-family: var(--font-family);
  font-size: var(--font-size-base);
  color: var(--color-charcoal);
  outline: none;
  transition: all var(--transition-base);
}

.intercom-input::placeholder,
.intercom-textarea::placeholder {
  color: var(--color-gray-500);
}

.intercom-input:disabled,
.intercom-textarea:disabled {
  cursor: not-allowed;
}

.intercom-input--prefix {
  padding-left: var(--space-10);
}

.intercom-input--suffix {
  padding-right: var(--space-10);
}

.intercom-textarea {
  padding: var(--space-3) var(--space-4);
  resize: vertical;
  min-height: 80px;
}

/* ========================================
   ICONS & AFFIXES
   ======================================== */
.intercom-input-prefix,
.intercom-input-suffix {
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-gray-500);
  transition: color var(--transition-fast);
}

.intercom-input-prefix {
  padding-left: var(--space-3);
  position: absolute;
  left: 0;
}

.intercom-input-suffix {
  padding-right: var(--space-3);
  position: absolute;
  right: 0;
}

.intercom-input-password-toggle {
  position: absolute;
  right: 0;
  padding: var(--space-2) var(--space-3);
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-gray-500);
  transition: color var(--transition-fast);
}

.intercom-input-password-toggle:hover {
  color: var(--color-gray-700);
}

.intercom-input-container:focus-within .intercom-input-password-toggle {
  color: var(--color-blue);
}

.intercom-input-container:focus-within .intercom-input-prefix,
.intercom-input-container:focus-within .intercom-input-suffix {
  color: var(--color-blue);
}

.intercom-input-icon {
  width: 16px;
  height: 16px;
}

/* ========================================
   MESSAGES
   ======================================== */
.intercom-input-error {
  font-size: var(--font-size-sm);
  color: var(--color-error);
  margin: 0;
}

.intercom-input-hint {
  font-size: var(--font-size-sm);
  color: var(--color-gray-500);
  margin: 0;
}
</style>
